/**
 * StatusPill — пилюля статуса подписки: цветная точка + текст на мягком
 * фоне. Палитра — из design_handoff_02_clients (раздел «Клиенты»).
 *
 * Статусные цвета заданы явными hex — это отдельная семантическая палитра
 * статусов (не совпадает с bg-success/warning темы), одинаковая в списке,
 * карточке и дашборде. Триал показывает «· X/30», если передан trialDay.
 */

import type {SubscriptionStatus} from '@services/clients';
import {cn} from '@lib/utils';

type StatusStyle = {label: string; className: string; dot: string};

const STATUS: Record<SubscriptionStatus, StatusStyle> = {
  active: {label: 'Активна', className: 'bg-[#E6F4EC] text-[#1B7A54]', dot: 'bg-[#1B7A54]'},
  trial: {label: 'Триал', className: 'bg-[#EAF0FD] text-[#2C5CDB]', dot: 'bg-[#2C5CDB]'},
  trial_expired: {
    label: 'Триал истёк',
    className: 'bg-[#FDEEE3] text-[#B4501B]',
    dot: 'bg-[#B4501B]',
  },
  awaiting_card: {
    label: 'Ожидает карту',
    className: 'bg-[#FBF3E4] text-[#B07908]',
    dot: 'bg-[#B07908]',
  },
  past_due: {label: 'Просрочена', className: 'bg-[#FBE9E7] text-[#C6362C]', dot: 'bg-[#C6362C]'},
  blocked: {label: 'Заблокирована', className: 'bg-[#F0EEEC] text-[#55524C]', dot: 'bg-[#55524C]'},
  cancelled: {label: 'Отменена', className: 'bg-[#F0F1F4] text-[#7C808A]', dot: 'bg-[#7C808A]'},
};

type StatusPillProps = {
  status: SubscriptionStatus;
  /** День триала (1..30) — добавляет суффикс «· X/30» к статусу trial. */
  trialDay?: number | null;
  className?: string;
};

export const StatusPill = ({status, trialDay, className}: StatusPillProps) => {
  const s = STATUS[status];
  const label = status === 'trial' && trialDay != null ? `${s.label} · ${trialDay}/30` : s.label;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-[9px] py-[3px] text-[12.5px] font-semibold',
        s.className,
        className,
      )}
    >
      <span className={cn('size-1.5 rounded-full', s.dot)} />
      {label}
    </span>
  );
};
